import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Grid,
  Typography,
  Container,
  IconButton,
  AppBar,
  Toolbar,
  Button,
  Stack,
  Divider,
  Chip,
  useTheme,
  alpha,
  Collapse,
} from '@mui/material';
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Home as HomeIcon,
  CheckCircle as CheckIcon,
  ArrowForward as ArrowForwardIcon,
  ExpandMore as ExpandMoreIcon,
  Hub as HubIcon,
  AdminPanelSettings as AdminIcon,
  ShoppingCart as ShoppingCartIcon,
  LocalActivity as TicketIcon,
  TravelExplore as TravelIcon,
  CardGiftcard as GiftIcon,
  Storefront as StorefrontIcon,
  VpnKey as VpnKeyIcon,
  ListAlt as ListAltIcon,
  Apps as AppsIcon,
  ShowChart as MarketMakerIcon,
  AccountBalanceWallet as WalletIcon,
  QrCode2 as QrCodeIcon,
} from '@mui/icons-material';
import { getAppRouteUrl } from '@kxdex/shared';
import { portals } from '@/config/portalConfig';
import { useThemeMode } from '@/theme/ThemeProvider';
import { HealthDot } from '@/components/HealthDot';
import CompanyFooter from '@/components/CompanyFooter';
import RtaDexLogo from '@/components/RtaDexLogo';
import { usePortalHealth } from '@/hooks/usePortalHealth';
import { HEALTH_TARGETS, AUXILIARY_TARGETS } from '@/config/portalHealth';
import RefreshIcon from '@mui/icons-material/Refresh';

const Brand = {
  blue: '#2563EB',
  gradient: 'linear-gradient(135deg, #2563EB 0%, #0D9488 100%)',
};

// Consumer portal highlight colors
const consumerBg = 'linear-gradient(135deg, #fff7ed 0%, #fef3c7 100%)';
const consumerBgDark = 'linear-gradient(135deg, #431407 0%, #451a03 100%)';
const exchangeBg = 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)';
const exchangeBgDark = 'linear-gradient(135deg, #0f172a 0%, #172554 100%)';

// Scenic spot images (placeholder patterns)
const scenicSpots = [
  { name: '滑雪', emoji: '🏔️' },
  { name: '人文', emoji: '🏯' },
  { name: '看海', emoji: '🌊' },
  { name: '登山', emoji: '🌄' },
];

const TestEntry: React.FC = () => {
  const navigate = useNavigate();
  const { mode, toggleTheme } = useThemeMode();
  const theme = useTheme();
  const isDark = mode === 'dark';

  const handlePlatformClick = () => {
    window.location.href = getAppRouteUrl('/total-admin/', 'login');
  };

  const openLogin = (basePath: string, redirect?: string) => {
    const url = new URL(getAppRouteUrl(basePath, 'login'), window.location.origin);
    if (redirect) {
      url.searchParams.set('redirect', redirect);
    }
    window.location.href = url.toString();
  };

  // Categorize portals
  const platformPortal = portals.find((p) => p.id === 'platform');
  const b2bPortals = portals.filter(
    (p) => !['platform', 'retail', 'exchange'].includes(p.id)
  );
  const exchangePortal = portals.find((p) => p.id === 'exchange');

  const [accountExpanded, setAccountExpanded] = useState(false);
  const [guideExpanded, setGuideExpanded] = useState<Record<string, boolean>>({});

  // 实时健康探测：挂载时并发探测所有 portal（前端静态 + 后端 health）
  const { healthMap, refresh: refreshHealth } = usePortalHealth();
  const healthOf = (portalId: string) => healthMap[portalId] || 'checking';
  const healthSummary = React.useMemo(() => {
    const all = [...HEALTH_TARGETS, ...AUXILIARY_TARGETS];
    const summary = { up: 0, partial: 0, down: 0, checking: 0, protected: 0 };
    all.forEach((t) => {
      summary[healthMap[t.portalId] || 'checking'] += 1;
    });
    return summary;
  }, [healthMap]);

  const toggleGuide = (key: string) => {
    setGuideExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const guideCards = [
    {
      key: 'login',
      icon: <VpnKeyIcon />,
      title: '登录说明',
      color: '#2563EB',
      content: (
        <Typography variant="body2" component="div" sx={{ lineHeight: 2 }}>
          所有子系统使用统一认证，入口不会公开或自动填充用户名、密码及钱包地址。<br />
          <strong>内部用户：</strong>使用管理员分配的个人账户登录，并按岗位角色访问对应子系统。<br />
          <strong>消费者：</strong>可在消费者端注册或使用已审核账户登录。<br />
          <strong>账户问题：</strong>请联系平台管理员执行重置、解锁或角色调整，禁止共享账户。
        </Typography>
      ),
    },
    {
      key: 'flow',
      icon: <ListAltIcon />,
      title: '典型操作流程',
      color: '#0D9488',
      content: (
        <Typography variant="body2" component="div" sx={{ lineHeight: 2 }}>
          <strong>1. 链上开户</strong> — 平台管理 → 账户与权限 → 授予角色（自动注册链上账户）<br />
          <strong>2. 门票发行</strong> — 景区管理 → BulkTicket.createSlot 创建票种 → mint / mintBatch 按 slot 批量铸造<br />
          <strong>3. 资金入金</strong> — 银行系统 → FiatToken铸造 → 充值到用户链上账户<br />
          <strong>4. 大宗合约撮合</strong> — 机构撮合端 → BulkExchange 下单 → 自动/手动撮合（Fiat/Bulk 权益托管）<br />
          <strong>5. 定价合约执行</strong> — BulkFixedPriceMarket 挂单 → 业务账户使用 FiatToken → BulkTicket 原子交付<br />
          <strong>6. 零售合约撮合</strong> — ConsumerRetailMarket 权益挂售 → TicketBridge.atomize → 业务账户获取 RetailTicket<br />
          <strong>7. 门票核销</strong> — 景区验票 → RetailTicket.verify 单张核销 → 核销后链上销毁<br />
          <strong>8. 退款合约处置</strong> — 业务账户 requestRefund 提交金额 → seller / issuer / 合约运营角色审批 → burn + controller 扣款<br />
          <strong>9. 应急清退</strong> — 账户冻结/停用后 → 合约运营/监管角色触发 emergencyCancel / emergencyExpire 退回托管资产<br />
          <strong>10. 同主体迁移</strong> — 合规排期 → 多签审批 → AccountMigration 执行跨地址迁移<br />
        </Typography>
      ),
    },
    {
      key: 'permissions',
      icon: <AdminIcon />,
      title: '账户权限说明',
      color: '#DC2626',
      content: (
        <Typography variant="body2" component="div" sx={{ lineHeight: 2 }}>
          <strong>平台管理员：</strong>负责角色授撤、白名单、迁移规则和治理级暂停；不能代替合约运营端执行撮合或结算<br />
          <strong>合约运营端：</strong>负责手工撮合、费率和运营参数；操作角色负责撤单、退款审批和应急清退<br />
          <strong>监管：</strong>负责冻结/解冻、多签审批、争议裁决和高风险迁移<br />
          <strong>银行：</strong>负责 FiatToken 铸造/销毁、银行兑回地址管理<br />
          <strong>景区：</strong>负责创建票种、铸票、RetailTicket 核销和本景区票种维护<br />
          <strong>做市商：</strong>参与大宗挂单、定价合约和零售供给，不承担系统治理职能<br />
          <strong>消费者：</strong>完成购票、持票、申请退票和平台内受控流转
        </Typography>
      ),
    },
    {
      key: 'subsystems',
      icon: <AppsIcon />,
      title: '子系统一览与技术架构',
      color: '#7C3AED',
      content: (
        <Typography variant="body2" component="div" sx={{ lineHeight: 2 }}>
          <strong>门户类：</strong>银行服务 · 景区管理 · 机构大宗撮合 · 合约运营 · 监管服务 · 消费者票务<br />
          <strong>撮合管理后台：</strong>全平台管理（用户/权限/配置）· 机构管理后台（审核/监控）· 消费者管理后台（订单/客服）<br />
          <strong>区块链：</strong>TIBCOS 国密联盟链 · 5 节点网络 · v2 合约体系：Auth / AccountStorage→Account / FiatStorage→FiatToken(ERC-20,d=2) / BulkTicket / RetailTicket / TicketBridge / BulkExchange / BulkFixedPriceMarket / ConsumerRetailMarket / ConsumerRefundMarket / AccountMigration · 单账户直接调用 + 受控划转 + 应急清退<br />
          <strong>后端：</strong>11 个 Spring Boot 微服务 · Java 11/21 · MySQL 8.0 · JWT · WeBASE-Sign<br />
          <strong>前端：</strong>10 个 React 18 应用 · TypeScript · MUI 6 · pnpm monorepo · Vite · Nginx
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Glass Navbar ── */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: alpha(theme.palette.background.paper, 0.85),
          color: 'text.primary',
          backdropFilter: 'blur(16px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ gap: 1 }}>
            <Box sx={{ mr: 2, flexShrink: 0, cursor: 'pointer' }} onClick={() => navigate('/')}>
              <RtaDexLogo size="small" showChineseName animated />
            </Box>
            <Box sx={{ flexGrow: 1 }} />

            <Button
              size="small"
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
              sx={{ mr: 1, textTransform: 'none', fontWeight: 600 }}
            >
              返回首页
            </Button>
            <IconButton onClick={toggleTheme} sx={{ color: 'text.secondary' }}>
              {isDark ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* ── Hero ── */}
      <Box
        sx={{
          background: isDark
            ? 'linear-gradient(180deg, #0f172a 0%, #020617 100%)'
            : 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)',
          pt: { xs: 6, md: 8 },
          pb: { xs: 4, md: 6 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Subtle background pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0, left: 0, right: 0, bottom: 0,
            opacity: isDark ? 0.03 : 0.04,
            backgroundImage: `radial-gradient(circle at 25% 25%, ${Brand.blue} 1px, transparent 1px), radial-gradient(circle at 75% 75%, #0D9488 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative' }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography
              variant="h3"
              fontWeight="bold"
              gutterBottom
              sx={{
                background: Brand.gradient,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '2rem', md: '3rem' },
              }}
            >
              RtaDex 智能合约撮合系统
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 760, mx: 'auto', mb: 2 }}>
              用于验证联盟链、智能合约与数字门票业务流程
            </Typography>
            <Chip
              label="许可型撮合网络 · 面向文旅权益数字化与机构协作"
              size="small"
              sx={{
                mb: 3,
                bgcolor: alpha('#d97706', isDark ? 0.18 : 0.1),
                color: isDark ? '#fbbf24' : '#92400e',
                border: `1px solid ${alpha('#d97706', 0.3)}`,
                fontWeight: 700,
              }}
            />
          </Box>

          <Box
            component="img"
            src="/brand/rtadex-logo.png"
            alt="RtaDex 智旅汇品牌主题图"
            data-testid="entry-brand-theme"
            sx={{
              display: 'block',
              width: { xs: '58%', sm: '52%' },
              maxWidth: 520,
              height: 'auto',
              mx: 'auto',
              mb: { xs: 4, md: 5 },
            }}
          />

          {/* Key stats */}
          <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 800, mx: 'auto' }}>
            {[
              { value: '9', label: '角色子系统', color: Brand.blue },
              { value: '11', label: '微服务节点', color: '#0D9488' },
              { value: '6', label: '联盟链节点', color: '#7C3AED' },
            ].map((stat) => (
              <Grid size={{ xs: 6, sm: 3 }} key={stat.label}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h3"
                    fontWeight="bold"
                    sx={{ color: stat.color, lineHeight: 1 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── 健康状态概览 ── */}
      <Box sx={{ py: { xs: 2, md: 3 }, bgcolor: isDark ? alpha('#0f172a', 0.6) : '#f1f5f9' }}>
        <Container maxWidth="lg">
          <Card
            variant="outlined"
            sx={{
              borderRadius: 2,
              p: { xs: 2, md: 2.5 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              gap: 2,
              borderColor: alpha(Brand.blue, 0.15),
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  子系统可达性
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  登录后可验证有权限访问的服务
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={refreshHealth}
                aria-label="刷新健康检查"
                data-testid="entry-health-refresh"
                sx={{ color: 'text.secondary' }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
              <Chip size="small" label={`已验证在线 ${healthSummary.up}`} sx={{ bgcolor: alpha('#22c55e', 0.12), color: '#16a34a', fontWeight: 600 }} />
              <Chip size="small" label={`需授权验证 ${healthSummary.protected}`} sx={{ bgcolor: alpha('#2563eb', 0.12), color: '#2563eb', fontWeight: 600 }} />
              <Chip size="small" label={`部分异常 ${healthSummary.partial}`} sx={{ bgcolor: alpha('#f59e0b', 0.12), color: '#d97706', fontWeight: 600 }} />
              <Chip size="small" label={`不可达 ${healthSummary.down}`} sx={{ bgcolor: alpha('#ef4444', 0.12), color: '#dc2626', fontWeight: 600 }} />
              <Chip size="small" label={`检测中 ${healthSummary.checking}`} sx={{ bgcolor: alpha('#94a3b8', 0.15), color: 'text.secondary', fontWeight: 600 }} />
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              {[...HEALTH_TARGETS, ...AUXILIARY_TARGETS].map((t) => (
                <Box key={t.portalId} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <HealthDot status={healthOf(t.portalId)} label={t.label} size={8} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                    {t.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Card>
        </Container>
      </Box>

      {/* ── RtaDex system introduction ── */}
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: isDark ? '#071f19' : '#eef4f0' }}>
        <Container maxWidth="lg">
          <Card
            component="button"
            type="button"
            data-testid="entry-system-introduction"
            onClick={() => {
              window.location.href = '/system-introduction/';
            }}
            sx={{
              width: '100%',
              minHeight: { xs: 217, md: 252 },
              p: 0,
              position: 'relative',
              overflow: 'hidden',
              border: `1px solid ${alpha('#6bc8a4', isDark ? 0.32 : 0.2)}`,
              borderRadius: 4,
              color: '#fff',
              textAlign: 'left',
              cursor: 'pointer',
              backgroundImage: [
                'linear-gradient(90deg, rgba(5, 30, 24, 0.96) 0%, rgba(5, 30, 24, 0.78) 48%, rgba(5, 30, 24, 0.16) 100%)',
                'url("/system-introduction/assets/00-system-hero-background.png")',
              ].join(', '),
              backgroundPosition: 'center',
              backgroundSize: 'cover',
              boxShadow: `0 18px 50px ${alpha('#071f19', isDark ? 0.42 : 0.2)}`,
              transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease',
              '&:hover': {
                transform: 'translateY(-3px)',
                borderColor: alpha('#72d0ad', 0.65),
                boxShadow: `0 24px 64px ${alpha('#071f19', isDark ? 0.52 : 0.28)}`,
              },
              '&:focus-visible': {
                outline: '3px solid #72d0ad',
                outlineOffset: 3,
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                width: { xs: '100%', md: '62%' },
                minHeight: { xs: 217, md: 252 },
                boxSizing: 'border-box',
                p: { xs: 2, sm: 2.5, md: 3.5 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: { xs: 1, md: 1.5 } }}>
                <Chip
                  icon={<HubIcon />}
                  label="系统全景介绍"
                  size="small"
                  sx={{
                    bgcolor: alpha('#72d0ad', 0.16),
                    color: '#a8e5cc',
                    border: `1px solid ${alpha('#72d0ad', 0.28)}`,
                    fontWeight: 800,
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
                <Chip
                  label="13 章 · 9 张流程图"
                  size="small"
                  sx={{ display: { xs: 'none', sm: 'inline-flex' }, bgcolor: alpha('#fff', 0.1), color: '#d6e6df', fontWeight: 700 }}
                />
              </Stack>
              <Typography
                variant="h3"
                fontWeight={800}
                sx={{ fontSize: { xs: '1.7rem', sm: '2.1rem', md: '2.5rem' }, lineHeight: 1.08, letterSpacing: '-0.04em', mb: { xs: 1, md: 1.25 } }}
              >
                一页看懂 RtaDex
              </Typography>
              <Typography sx={{ color: '#c8d9d2', lineHeight: 1.55, maxWidth: 650, mb: { xs: 1.25, md: 2 }, display: '-webkit-box', WebkitLineClamp: { xs: 2, md: 2 }, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                从门票发行、合约撮合、消费者钱包，到多次票、家庭票、现场核销、离线 Edge Hub 与链上对账，系统说明业务边界、技术架构和当前成熟度。
              </Typography>
              <Button
                component="span"
                variant="contained"
                size="small"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  bgcolor: '#72d0ad',
                  color: '#071f19',
                  fontWeight: 800,
                  textTransform: 'none',
                  px: 2.5,
                  '&:hover': { bgcolor: '#8ddcbe' },
                }}
              >
                打开完整系统介绍
              </Button>
            </Box>
          </Card>
        </Container>
      </Box>

      {/* ── 账户体系架构 (Card style, collapsible) ── */}
      <Box sx={{ py: { xs: 3, md: 4 }, bgcolor: isDark ? alpha('#1e293b', 0.5) : '#f8fafc' }}>
        <Container maxWidth="lg">
          {/* ── 系统使用指南 (Card style, collapsible) ── */}
          <Typography variant="h5" fontWeight="bold" textAlign="center" sx={{ mb: 3 }}>
            使用指南
          </Typography>

          <Grid container spacing={2.5} alignItems="flex-start">
            {guideCards.map((card) => (
              <Grid size={{ xs: 12, sm: 6 }} key={card.key}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `1px solid ${alpha(card.color, 0.15)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: `0 8px 24px ${alpha(card.color, 0.12)}`,
                      borderColor: alpha(card.color, 0.3),
                    },
                  }}
                >
                  {/* Card Header - clickable */}
                  <Box
                    onClick={() => toggleGuide(card.key)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2,
                      p: 2.5, cursor: 'pointer',
                      bgcolor: alpha(card.color, 0.06),
                      '&:hover': { bgcolor: alpha(card.color, 0.1) },
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <Box sx={{
                      width: 40, height: 40, borderRadius: '50%',
                      bgcolor: alpha(card.color, 0.12),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: card.color, flexShrink: 0,
                    }}>
                      {React.cloneElement(card.icon, { sx: { fontSize: 20 } })}
                    </Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="text.primary" sx={{ flex: 1 }}>
                      {card.title}
                    </Typography>
                    <ExpandMoreIcon
                      sx={{
                        color: 'text.secondary', fontSize: 22,
                        transform: guideExpanded[card.key] ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                      }}
                    />
                  </Box>
                  {/* Collapsible content */}
                  <Collapse in={!!guideExpanded[card.key]}>
                    <Box sx={{ p: 2.5, borderTop: `1px solid ${alpha(card.color, 0.1)}` }}>
                      {card.content}
                    </Box>
                  </Collapse>
                </Card>
              </Grid>
            ))}
          </Grid>
          {/* 账户体系架构 - liquid glass collapsible card, full width */}
          <Card
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              mt: 4,
              border: `1px solid ${alpha('#7C3AED', 0.15)}`,
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: `0 8px 24px ${alpha('#7C3AED', 0.12)}`,
                borderColor: alpha('#7C3AED', 0.3),
              },
            }}
          >
            {/* Card Header - clickable */}
            <Box
              onClick={() => setAccountExpanded(!accountExpanded)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                p: 2.5, cursor: 'pointer',
                bgcolor: alpha('#7C3AED', 0.06),
                '&:hover': { bgcolor: alpha('#7C3AED', 0.1) },
                transition: 'background-color 0.2s',
              }}
            >
              <Box sx={{
                width: 40, height: 40, borderRadius: '50%',
                bgcolor: alpha('#7C3AED', 0.12),
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#7C3AED', flexShrink: 0,
              }}>
                <HubIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                  多平台统一账户体系架构
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Chip label="32 用户" size="small" sx={{ bgcolor: alpha('#2563EB', 0.1), color: '#2563EB', fontWeight: 600, fontSize: '0.7rem' }} />
                <Chip label="32 钱包" size="small" sx={{ bgcolor: alpha('#059669', 0.1), color: '#059669', fontWeight: 600, fontSize: '0.7rem' }} />
                <Chip label="5 节点" size="small" sx={{ bgcolor: alpha('#7C3AED', 0.1), color: '#7C3AED', fontWeight: 600, fontSize: '0.7rem' }} />
              </Stack>
              <ExpandMoreIcon
                sx={{
                  color: 'text.secondary', fontSize: 22,
                  transform: accountExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s',
                }}
              />
            </Box>
            {/* Collapsible content */}
            <Collapse in={accountExpanded}>
              <Box sx={{ p: { xs: 2, md: 4 }, borderTop: `1px solid ${alpha('#7C3AED', 0.1)}` }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, mb: 3 }}>
                  平台采用三层账户映射体系，将传统登录账户（sys_user）与链上钱包（sys_wallet）及私钥托管服务（WeBASE-Sign）逐层绑定，实现用户身份、链上资产、交易签名的安全隔离与统一管理。
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, justifyContent: 'center', alignItems: 'stretch' }}>
                  <Card variant="outlined" sx={{ flex: 1, p: 2, borderColor: '#2563EB', borderWidth: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="#2563EB" sx={{ mb: 1 }}>1. sys_user（登录账户）</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>MySQL · JWT 认证</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" component="div" sx={{ lineHeight: 1.8 }}>• 用户名 / 密码登录<br/>• 角色：ROOT / BANK / SCENIC / ...<br/>• 权限：菜单 + API 级别控制<br/>• 支持 MFA、账户锁定</Typography>
                  </Card>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h5" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>→</Typography>
                    <Typography variant="h5" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' } }}>↓</Typography>
                  </Box>
                  <Card variant="outlined" sx={{ flex: 1, p: 2, borderColor: '#059669', borderWidth: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="#059669" sx={{ mb: 1 }}>2. sys_wallet（钱包映射）</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>MySQL · 1:N 关系</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" component="div" sx={{ lineHeight: 1.8 }}>• 链上地址 0x...<br/>• 链上角色（Auth 合约）<br/>• 单账户模型<br/>• 一个用户可绑多个钱包，同主体迁移受控执行</Typography>
                  </Card>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="h5" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>→</Typography>
                    <Typography variant="h5" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' } }}>↓</Typography>
                  </Box>
                  <Card variant="outlined" sx={{ flex: 1, p: 2, borderColor: '#d97706', borderWidth: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} color="#d97706" sx={{ mb: 1 }}>3. WeBASE-Sign（私钥托管）</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>AES 加密 · 签名服务</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" component="div" sx={{ lineHeight: 1.8 }}>• PEM 私钥加密存储<br/>• signUserId 关联<br/>• 交易签名代理<br/>• 私钥不出服务器</Typography>
                  </Card>
                </Box>
              </Box>
            </Collapse>
          </Card>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════════════════
          Section 1: Consumer Portal (B2C) - Prominent, inviting design
          ══════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          background: isDark ? consumerBgDark : consumerBg,
          py: 6,
          borderTop: `1px solid ${alpha('#ff6b35', 0.15)}`,
          borderBottom: `1px solid ${alpha('#ff6b35', 0.15)}`,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
              <Chip
                icon={<StorefrontIcon />}
                label="消费者入口"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  bgcolor: alpha('#ff6b35', 0.12),
                  color: isDark ? '#fb923c' : '#ea580c',
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
              <HealthDot status={healthOf('retail')} label="消费者市场" />
            </Stack>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              发现精彩旅程，畅享数字门票
            </Typography>
          </Box>

          {/* Consumer main card */}
          <Card
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              border: 'none',
              boxShadow: isDark
                ? `0 8px 32px ${alpha('#ff6b35', 0.2)}`
                : `0 8px 32px ${alpha('#ff6b35', 0.15)}`,
            }}
          >
            <Grid container>
              {/* Left: Info */}
              <Grid size={{ xs: 12, md: 7 }}>
                <Box sx={{ p: { xs: 3.5, md: 4.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                    <Box
                      sx={{
                        width: 56, height: 56, borderRadius: 3,
                        background: 'linear-gradient(135deg, #ff6b35 0%, #f59e0b 100%)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff',
                      }}
                    >
                      <ShoppingCartIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight="bold">
                        门票市场
                      </Typography>
                    </Box>
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {[
                      { icon: <TicketIcon />, label: '门票市场', desc: '浏览热门景区门票' },
                      { icon: <TravelIcon />, label: '我的订单', desc: '查看购票与合约撮合记录' },
                      { icon: <GiftIcon />, label: '我的资产', desc: '管理数字门票资产' },
                    ].map((item, i) => (
                      <Grid size={{ xs: 12, sm: 4 }} key={i}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#ff6b35', 0.04),
                            border: `1px solid ${alpha('#ff6b35', 0.1)}`,
                            textAlign: 'center',
                          }}
                        >
                          <Box sx={{ color: '#f97316', mb: 1 }}>{item.icon}</Box>
                          <Typography variant="body2" fontWeight={600}>{item.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Box
                    component="button"
                    type="button"
                    onClick={() => {
                      window.location.href = getAppRouteUrl('/consumer-retail/', 'wallet');
                    }}
                    data-testid="entry-consumer-wallet"
                    sx={{
                      width: '100%',
                      mb: 3,
                      p: { xs: 2, sm: 2.5 },
                      border: `1px solid ${alpha('#0f5132', isDark ? 0.72 : 0.24)}`,
                      borderRadius: 3,
                      background: isDark
                        ? 'linear-gradient(135deg, #082f22 0%, #123f31 100%)'
                        : 'linear-gradient(135deg, #ecfdf5 0%, #fef3c7 100%)',
                      color: 'text.primary',
                      font: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        borderColor: '#c99a2e',
                        boxShadow: `0 10px 28px ${alpha('#0f5132', 0.18)}`,
                      },
                      '&:focus-visible': {
                        outline: '3px solid',
                        outlineColor: alpha('#c99a2e', 0.48),
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2.5,
                        bgcolor: '#0f5132',
                        color: '#f4cf72',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <WalletIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="subtitle1" fontWeight={800}>
                          移动票夹 H5
                        </Typography>
                        <Chip
                          size="small"
                          icon={<QrCodeIcon />}
                          label="动态验票码"
                          sx={{
                            bgcolor: alpha('#c99a2e', isDark ? 0.2 : 0.14),
                            color: isDark ? '#f4cf72' : '#806013',
                            fontWeight: 700,
                            '& .MuiChip-icon': { color: 'inherit' },
                          }}
                        />
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        手机打开门票钱包，展示单次票、多次票与家庭票的实时核销二维码
                      </Typography>
                    </Box>
                    <ArrowForwardIcon sx={{ color: '#c99a2e', flexShrink: 0 }} />
                  </Box>

                  <Grid container spacing={1.5}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box
                        onClick={(event) => {
                          event.stopPropagation();
                          openLogin('/consumer-retail/');
                        }}
                        data-testid="entry-consumer-login-retail"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#ff6b35', 0.05),
                          border: `1px solid ${alpha('#ff6b35', 0.15)}`,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: alpha('#ff6b35', 0.1),
                            borderColor: '#ff6b35',
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 16px ${alpha('#ff6b35', 0.2)}`,
                          },
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#ea580c' }}>
                            消费者登录
                          </Typography>
                          <ArrowForwardIcon sx={{ fontSize: 16, color: '#ea580c' }} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          使用个人账户或注册后登录
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box
                        onClick={(event) => {
                          event.stopPropagation();
                          openLogin('/consumer-retail/', '/listings');
                        }}
                        data-testid="entry-consumer-login-market-maker"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#00695c', 0.05),
                          border: `1px solid ${alpha('#00695c', 0.15)}`,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            bgcolor: alpha('#00695c', 0.1),
                            borderColor: '#00695c',
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 16px ${alpha('#00695c', 0.2)}`,
                          },
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#00695c' }}>
                            做市商登录
                          </Typography>
                          <ArrowForwardIcon sx={{ fontSize: 16, color: '#00695c' }} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          登录后进入零售票源管理
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Right: Scenic spot highlights */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Box
                  sx={{
                    height: '100%',
                    minHeight: 280,
                    background: isDark
                      ? 'linear-gradient(135deg, #1c1917 0%, #292524 100%)'
                      : 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%)',
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 2 }}>
                    热门景区
                  </Typography>
                  <Grid container spacing={1.5}>
                    {scenicSpots.map((spot, i) => (
                      <Grid size={6} key={i}>
                        <Box
                          sx={{
                            bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#fff', 0.8),
                            borderRadius: 2,
                            p: 2,
                            textAlign: 'center',
                            border: `1px solid ${alpha('#ff6b35', 0.1)}`,
                          }}
                        >
                          <Typography fontSize={32} sx={{ mb: 0.5 }}>{spot.emoji}</Typography>
                          <Typography variant="body2" fontWeight={600}>{spot.name}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      链上确权 &bull; 安全可信
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      {exchangePortal && (
        <Box
          sx={{
            background: isDark ? exchangeBgDark : exchangeBg,
            mt: { xs: 3, md: 4 },
            py: 6,
            borderTop: `1px solid ${alpha(exchangePortal.color, 0.15)}`,
            borderBottom: `1px solid ${alpha(exchangePortal.color, 0.15)}`,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                <Chip
                  icon={React.cloneElement(exchangePortal.icon, { sx: { fontSize: 18 } })}
                  label="合约撮合"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.85rem',
                    bgcolor: alpha(exchangePortal.color, 0.12),
                    color: exchangePortal.color,
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
                <HealthDot status={healthOf('exchange')} label="大宗合约撮合" />
              </Stack>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                撮合合约、整包挂牌与链上清算验证
              </Typography>
            </Box>

            <Card
              sx={{
                borderRadius: 4,
                overflow: 'hidden',
                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                border: 'none',
                boxShadow: isDark
                  ? `0 8px 32px ${alpha(exchangePortal.color, 0.2)}`
                  : `0 8px 32px ${alpha(exchangePortal.color, 0.15)}`,
                '&:hover': {
                  transform: 'translateY(-6px)',
                  boxShadow: `0 20px 48px ${alpha(exchangePortal.color, 0.25)}`,
                },
              }}
            >
              <Grid container>
                <Grid size={12}>
                  <Box sx={{ p: { xs: 3.5, md: 4.5 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                      <Box
                        sx={{
                          width: 56, height: 56, borderRadius: 3,
                          background: `linear-gradient(135deg, ${exchangePortal.color} 0%, #1d4ed8 100%)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff',
                        }}
                      >
                        {React.cloneElement(exchangePortal.icon, { sx: { fontSize: 28 } })}
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold">
                          {exchangePortal.name}
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {[
                        { icon: <StorefrontIcon />, label: '大宗合约挂单', desc: '景区与机构发布文旅权益' },
                        { icon: <MarketMakerIcon />, label: '订单簿合约', desc: '触发 BulkExchange 智能撮合' },
                        { icon: <TicketIcon />, label: '定价合约', desc: '查看 BulkFixedPriceMarket 执行结果' },
                        { icon: <ListAltIcon />, label: '执行记录', desc: '查看挂单、执行与履约记录' },
                      ].map((item, i) => (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              bgcolor: isDark ? alpha('#fff', 0.04) : alpha(exchangePortal.color, 0.04),
                              border: `1px solid ${alpha(exchangePortal.color, 0.1)}`,
                              textAlign: 'center',
                            }}
                          >
                            <Box sx={{ color: exchangePortal.color, mb: 1 }}>{item.icon}</Box>
                            <Typography variant="body2" fontWeight={600}>{item.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{item.desc}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          onClick={() => {
                            openLogin('/institutional-market/');
                          }}
                          data-testid="entry-exchange-login-market-maker"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#00695c', 0.05),
                            border: `1px solid ${alpha('#00695c', 0.15)}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: alpha('#00695c', 0.1),
                              borderColor: '#00695c',
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 16px ${alpha('#00695c', 0.2)}`,
                            },
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#00695c' }}>
                              做市商登录
                            </Typography>
                            <ArrowForwardIcon sx={{ fontSize: 16, color: '#00695c' }} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            使用已授权做市商账户
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box
                          onClick={() => {
                            openLogin('/institutional-market/');
                          }}
                          data-testid="entry-exchange-login-scenic"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: isDark ? alpha('#fff', 0.04) : alpha('#2e7d32', 0.05),
                            border: `1px solid ${alpha('#2e7d32', 0.15)}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              bgcolor: alpha('#2e7d32', 0.1),
                              borderColor: '#2e7d32',
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 16px ${alpha('#2e7d32', 0.2)}`,
                            },
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#2e7d32' }}>
                              景区登录
                            </Typography>
                            <ArrowForwardIcon sx={{ fontSize: 16, color: '#2e7d32' }} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary">
                            使用已授权景区账户
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 2.5, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        文旅权益 &bull; 合约撮合 &bull; 链上结算协同
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Container>
        </Box>
      )}

      {/* ══════════════════════════════════════════════════════════════
          Section 2: B2B Portals - Professional, structured
          ══════════════════════════════════════════════════════════════ */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ mb: 4 }}>
          <Chip
            label="企业服务端"
            size="small"
            sx={{
              mb: 1.5,
              fontWeight: 600,
              bgcolor: alpha(Brand.blue, 0.08),
              color: Brand.blue,
            }}
          />
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            智能合约撮合子系统
          </Typography>
          <Typography variant="body2" color="text.secondary">
            面向合约运营、景区、做市商和监管机构的专业业务后台 — 使用管理员分配的授权账户登录
          </Typography>
        </Box>

        

        {/* Platform Management Card */}
        {platformPortal && (
          <Box sx={{ mb: 3 }}>
            <Card
              onClick={handlePlatformClick}
              data-testid="entry-platform-card-platform"
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                border: `1px solid ${alpha(platformPortal.color, 0.15)}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 16px 40px ${alpha(platformPortal.color, 0.15)}`,
                  borderColor: alpha(platformPortal.color, 0.3),
                },
              }}
            >
              <Box sx={{
                background: `linear-gradient(135deg, ${platformPortal.color} 0%, #5c6bc0 100%)`,
                p: { xs: 3, md: 4 },
                color: '#fff',
              }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box sx={{
                        width: 48, height: 48, borderRadius: '50%',
                        bgcolor: alpha('#fff', 0.15), display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}>
                        <HubIcon sx={{ fontSize: 24 }} />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6" fontWeight="bold">
                            {platformPortal.name}
                          </Typography>
                          <HealthDot status={healthOf(platformPortal.id)} label={platformPortal.name} />
                        </Box>
                        <Typography variant="body2" sx={{ opacity: 0.85 }}>
                          {platformPortal.subtitle}
                        </Typography>
                      </Box>
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {platformPortal.menuItems.map((item) => (
                        <Chip
                          key={item.path}
                          label={item.text}
                          size="small"
                          sx={{
                            bgcolor: alpha('#fff', 0.15),
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                          }}
                        />
                      ))}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Box sx={{
                      bgcolor: alpha('#fff', 0.1),
                      borderRadius: 2, p: 2,
                      backdropFilter: 'blur(8px)',
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <AdminIcon sx={{ fontSize: 18 }} />
                        <Typography variant="body2" fontWeight={600}>登录账户</Typography>
                      </Box>
                      <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', lineHeight: 1.8 }}>
                        使用管理员分配的个人账户
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Card>
          </Box>
        )}

        {/* B2B Portal Grid */}
        <Grid container spacing={2.5}>
          {b2bPortals.map((portal) => {
            return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={portal.id}>
              <Card
                onClick={() => openLogin(portal.basePath + '/')}
                data-testid={`entry-portal-card-${portal.id}`}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: `1px solid ${alpha(portal.color, 0.1)}`,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 28px ${alpha(portal.color, 0.15)}`,
                    borderColor: alpha(portal.color, 0.25),
                  },
                  '&:hover .enter-hint': { opacity: 1 },
                  '&:hover .portal-subtitle': { opacity: 0 },
                }}
              >
                {/* Header */}
                <Box sx={{
                  bgcolor: alpha(portal.color, 0.06),
                  p: 2.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '50%',
                    bgcolor: alpha(portal.color, 0.12),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: portal.color,
                  }}>
                    {React.cloneElement(portal.icon, { sx: { fontSize: 22 } })}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                        {portal.name}
                      </Typography>
                      <HealthDot status={healthOf(portal.id)} label={portal.name} size={9} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {portal.subtitle}
                    </Typography>
                  </Box>
                </Box>

                {/* Body */}
                <Box sx={{ p: 2.5, position: 'relative' }}>
                  <Stack spacing={1} sx={{ mb: 1.5 }}>
                    {portal.menuItems.slice(0, 4).map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CheckIcon sx={{ fontSize: 14, color: portal.color, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
                          {item.text}
                        </Typography>
                      </Box>
                    ))}
                    {portal.menuItems.length > 4 && (
                      <Typography variant="caption" color="text.disabled" sx={{ pl: 3.5 }}>
                        +{portal.menuItems.length - 4} 更多功能
                      </Typography>
                    )}
                  </Stack>

                  {/* Authentication entry */}
                  <Box sx={{
                    mt: 1.5, p: 1.5, borderRadius: 1.5,
                    bgcolor: alpha(portal.color, 0.04),
                    border: `1px solid ${alpha(portal.color, 0.1)}`,
                  }}>
                    <Typography variant="caption" fontWeight={600} color={portal.color} sx={{ mb: 0.5, display: 'block' }}>
                      安全登录
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      使用管理员分配且与当前岗位匹配的个人账户
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      </Container>

      <CompanyFooter />
    </Box>
  );
};

export default TestEntry;
